import { ApolloClient, ApolloProvider, InMemoryCache, NormalizedCacheObject, ServerError, from } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { relayStylePagination } from '@apollo/client/utilities'
import { CachePersistor, LocalStorageWrapper } from 'apollo3-cache-persist'
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ApolloPersistorProvider } from '@/apollo/ApolloPersistorProvider'
import createOperationNameLink from '@/apollo/links/createOperationNameLink'
import createRefererLink from '@/apollo/links/createRefererLink'
import createSentryLink from '@/apollo/links/createSentryLink'
import { createVariableTransformLink } from '@/apollo/links/createVariablesTransformationLink'
import { apolloRelayStylePagination } from '@/apollo/utils/apolloRelayStylePagination'
import { dataPersistenceMapper } from '@/apollo/utils/dataPersistenceMapper'
import { Spinner } from '@/modules/requisitions/components/spinner/Spinner'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { useAlert } from '@/modules/shared/hooks/useAlert'

type ApolloProviderWrapperProps = PropsWithChildren

function ApolloProviderWrapper(props: ApolloProviderWrapperProps) {
  const { children } = props
  const [apolloClient, setApolloClient] = useState<ApolloClient<NormalizedCacheObject>>()
  const [apolloPersistor, setApolloPersistor] = useState<CachePersistor<NormalizedCacheObject>>()

  const { t } = useTranslation()
  const { alertDialog, genericNotAuthorizedAlert } = useAlert()

  useEffect(() => {
    if (apolloClient) {
      const handleClearCache = () => {
        apolloClient.clearStore() // or apolloClient.resetStore() if you want to re-execute active queries
      }

      window.addEventListener('clearApolloCache', handleClearCache)

      return () => {
        window.removeEventListener('clearApolloCache', handleClearCache)
      }
    }
  }, [apolloClient])

  useEffect(() => {
    if (apolloPersistor) {
      const handlePurgePersistor = async () => {
        await apolloPersistor.purge()
      }

      window.addEventListener('purgeApolloPersistor', handlePurgePersistor)

      return () => {
        window.removeEventListener('purgeApolloPersistor', handlePurgePersistor)
      }
    }
  }, [apolloPersistor])

  useEffect(() => {
    async function init() {
      const errorLink = onError((error) => {
        // eslint-disable-next-line deprecation/deprecation
        const networkError = error.networkError as ServerError
        // eslint-disable-next-line deprecation/deprecation
        const isNotAuthorized = (error?.graphQLErrors || []).some(
          (e) => e?.extensions?.code === 401 && e?.extensions?.status === 'Unauthorized'
        )

        // Any request met with a 401 response
        if (networkError?.statusCode === 401) {
          // Clear persisted cache data on 401
          window.dispatchEvent(new Event('purgeApolloPersistor'))

          // These operations shouldn't raise an alert dialog
          if (
            error.operation.operationName === 'GetSessionUser' ||
            error.operation.operationName === 'GetCurrentPurchaser' ||
            error.operation.operationName === 'SignOut'
          ) {
            return
          }
          return alertDialog({
            type: 'error',
            title: t('alert.unauthenicated.title', 'Please Sign In Again'),
            message: t('alert.unauthenicated.message', 'Your session has expired, please sign in again.'),
            buttonText: t('general.signIn', 'Sign In'),
            onButtonClick() {
              window.location.reload()
            },
          })
        }

        if (networkError?.statusCode >= 500) {
          // this is to help us debug these issues when they appear in Sentry, without triggering more Sentry errors
          console.log(error)

          // These operations shouldn't raise an alert dialog
          if (error.operation.operationName === 'GetDashboardStats') return

          return alertDialog({
            type: 'error',
            title: t('general.internalServerError', 'Internal Server Error'),
            message: t(
              'alert.internalServerError.message',
              'Sorry but there was an internal server error. Please try again later.'
            ),
            buttonText: t('general.okay', 'Okay'),
          })
        }

        if (isNotAuthorized || networkError?.statusCode === 403) {
          return genericNotAuthorizedAlert()
        }

        if (networkError) {
          const operationName = error.operation.operationName
          const operationErrorMessage = networkError?.message
          const statusCode = networkError?.statusCode

          networkError.message = `${operationName} (Status code ${statusCode}): "${operationErrorMessage}"`
        }

        // this is to help us debug these issues when they appear in Sentry, without triggering more Sentry errors
        console.log(error)
        // All other errors
        // `handleErrorLocally` will decide whether to show a global error or handle and show the error locally
        if (!networkError && !error.operation.getContext().handleErrorLocally) alertDialog({ type: 'error' })
      })

      const operationNameLink = createOperationNameLink(
        PURCHASE_PLUS_GRAPHQL_API,
        import.meta.env.VITE_APP_VERCEL_ENV || ''
      )
      const variableTransformLink = createVariableTransformLink()
      const sentryLink = createSentryLink()
      const refererLink = createRefererLink()
      const uploadLink = createUploadLink({
        uri: PURCHASE_PLUS_GRAPHQL_API,
        credentials: 'include',
      })

      const apolloCache = new InMemoryCache({
        possibleTypes: {
          ProductInterface: ['Product', 'StockItem', 'Ingredient'],
          CataloguedProductInterface: ['BuyListProduct', 'PricedCataloguedProduct'],
          AccountingDocumentInterface: ['Invoice', 'CreditNote'],
          OrganisationInterface: ['Purchaser', 'Supplier'],
          InventoryLocationInterface: [
            'AccountingLocation',
            'ExpenseLocation',
            'Location',
            'OpeningLocation',
            'PreparationLocation',
            'RecipeLocation',
            'SaleExpenseLocation',
            'StockLocation',
            'SupplyLocation',
            'VarianceLocation',
          ],
        },
        typePolicies: {
          Query: {
            fields: {
              categories: relayStylePagination(['filter', 'filters']),
              organisations: relayStylePagination(),
              suppliers: relayStylePagination(),
              products: relayStylePagination(),
              productImports: relayStylePagination(),
              financeExportFormats: relayStylePagination(),
              accountsPayableSystems: relayStylePagination(),
              financeExportExecutions: relayStylePagination(),
              barcodes: relayStylePagination(),
              stockItems: relayStylePagination(),
              stockLocations: apolloRelayStylePagination(),
              transfers: relayStylePagination(),
              users: relayStylePagination(),
              tradeRelationships: relayStylePagination(),
              pipes: relayStylePagination(),
              stockTakes: relayStylePagination(),
              recipes: relayStylePagination(),
              menus: relayStylePagination(),
              organisationImports: relayStylePagination(),
              reports: relayStylePagination(),
              workflows: relayStylePagination(['filters']),
              procurementProducts: relayStylePagination(),
              ingredients: relayStylePagination(),
              aiScanningJobs: relayStylePagination(),
              pricedCatalogues: relayStylePagination(),
              tradeRelationshipPricedCatalogues: apolloRelayStylePagination(),
              aiPrompts: apolloRelayStylePagination(),
              allPricedCatalogues: relayStylePagination(),

              pointOfSaleItems: relayStylePagination(),
              pointOfSaleOutlets: relayStylePagination(),
              billingGroups: relayStylePagination(),
              pointOfSaleBatches: relayStylePagination(),
              pendingInvoices: relayStylePagination(),
              processDocuments: relayStylePagination(),
              purchaseOrders: relayStylePagination(),
              allInvoices: relayStylePagination(),
              userImports: relayStylePagination(),
              deliveryAddresses: relayStylePagination(),
              orders: relayStylePagination(),
              publicShops: relayStylePagination(),
              purchasers: relayStylePagination(),
              invoices: relayStylePagination(),
              allTradeRelationships: relayStylePagination(),
              creditNotes: relayStylePagination(),
              quotes: relayStylePagination(),
              buyLists: relayStylePagination(),
              tags: relayStylePagination(),
              paperlessEmails: relayStylePagination(),
              accounts: relayStylePagination(),
              organisationAccounts: relayStylePagination(),
              productCleaningJobs: relayStylePagination(),
              apiKeys: relayStylePagination(),
              budgets: relayStylePagination(),
              receivingDocuments: relayStylePagination(),
              requisitions: relayStylePagination(),
              productTags: relayStylePagination(),
              allergens: relayStylePagination(),

              dashboardStats: {
                // dashboardStats doesnt have an id so we will store data as a flat structure without normalizing it
                merge: (_existing, incoming) => {
                  return incoming
                },
              },
            },
          },
          PublicShop: {
            fields: {
              publicCataloguedProducts: relayStylePagination(),
            },
          },
          WorkflowRank: {
            fields: {
              workflowRankMemberships: relayStylePagination(),
            },
          },
          Catalogue: {
            fields: {
              cataloguedProducts: relayStylePagination(),
            },
          },
          Category: {
            fields: {
              categories: relayStylePagination(['filter']),
            },
          },
          Holder: {
            fields: {
              stockLocations: relayStylePagination(),
              stockTakes: relayStylePagination(),
            },
          },
          Preference: {
            keyFields: ['key'],
          },
          Purchaser: {
            fields: {
              accounts: relayStylePagination(),
              allAccounts: relayStylePagination(),
              availableCatalogues: relayStylePagination(),
              availableProducts: relayStylePagination(),
              catalogues: relayStylePagination(),
              creditNotes: relayStylePagination(),
              deliveryAddresses: relayStylePagination(),
              invoices: relayStylePagination(),
              pricedCatalogues: relayStylePagination(),
              purchaseOrders: relayStylePagination(),
              receivingDocuments: relayStylePagination(),
              requisitions: relayStylePagination(),
              requisitionsAwaitingMyApproval: relayStylePagination(),
              supplierRelationships: relayStylePagination(),
              supplierRelationshipsWithCatalogue: relayStylePagination(),
              users: relayStylePagination(),
              flatAccountCodes: relayStylePagination(),
            },
          },
          BuyList: {
            fields: {
              buyListProducts: relayStylePagination(),
              buyListImports: relayStylePagination(),
              buyListExports: relayStylePagination(),
            },
          },
          Requisition: {
            fields: {
              attachments: relayStylePagination(),
              possibleRequisitionLines: relayStylePagination(),
            },
          },
          TransferItem: {
            keyFields: (object) => {
              if (object.id) return ['id']
              return ['stockItem', ['id']]
            },
            fields: {
              comments: relayStylePagination(),
            },
          },
          RequisitionLine: {
            keyFields: ['uuid'],
          },
          StockTake: {
            fields: {
              stockCounts: relayStylePagination(),
              stocktakeImports: relayStylePagination(),
              stocktakeExports: relayStylePagination(),
            },
          },
          Supplier: {
            fields: {
              availableProducts: relayStylePagination(),
              publicCataloguedProducts: relayStylePagination(),
              purchasers: relayStylePagination(),
              users: relayStylePagination(),
            },
          },
          PricedCatalogue: {
            fields: {
              pricedCataloguedProducts: relayStylePagination(),
              cataloguedProducts: relayStylePagination(),
              catalogueImports: relayStylePagination(),
              catalogueExports: relayStylePagination(),
              tradeRelationshipPricedCatalogues: relayStylePagination(),
            },
          },
          PurchaseOrder: {
            fields: {
              lines: relayStylePagination(),
              purchaseOrderLineItems: relayStylePagination(),
              deliveries: relayStylePagination(),
              invoices: relayStylePagination(),
            },
          },
          Invoice: {
            fields: {
              attachments: relayStylePagination(),
              invoiceLineItems: relayStylePagination(['filters', 'sorts']),
            },
          },
          ReceivingDocument: {
            fields: {
              receivingDocumentLineItems: relayStylePagination(),
            },
          },
          Report: {
            fields: {
              executions: relayStylePagination(),
              templates: relayStylePagination(),
            },
          },
          User: {
            fields: {
              organisations: relayStylePagination(),
              userOrganisations: relayStylePagination(),
              pagePreference: {
                merge: (_existing, incoming) => {
                  return incoming
                },
              },
            },
          },
          CreditNote: {
            fields: {
              attachments: relayStylePagination(),
              creditNoteLineItems: relayStylePagination(),
            },
          },
          AccountsPayableSystem: {
            fields: {
              flatAccountCodes: relayStylePagination(),
              suppliers: relayStylePagination(),
              purchasers: relayStylePagination(),
              vendors: relayStylePagination(),
              accounts: relayStylePagination(),
              taxCodes: relayStylePagination(),
            },
          },
          Location: {
            fields: {
              stockLevels: relayStylePagination(),
              valueAdjustments: relayStylePagination(),
              transfers: relayStylePagination(),
            },
          },
          Transfer: {
            fields: {
              transferAttachments: relayStylePagination(),
              transferItems: relayStylePagination(),
            },
          },
          StockItem: {
            fields: {
              substituteProducts: relayStylePagination(),
            },
          },
          StockLevel: {
            fields: {
              transferItems: relayStylePagination(),
            },
          },
          Product: {
            fields: {
              substituteProducts: relayStylePagination(),
              similarProducts: relayStylePagination(),
            },
          },
          Recipe: {
            fields: {
              lineItems: relayStylePagination(),
              menus: relayStylePagination(),
            },
          },
          Menu: {
            fields: {
              recipes: relayStylePagination(),
            },
          },
          AiPrompt: {
            fields: {
              organisations: relayStylePagination(),
            },
          },
          Workflow: {
            fields: {
              assignments: relayStylePagination(),
              executions: relayStylePagination(),
            },
          },
          PointOfSaleBatch: {
            fields: {
              pointOfSaleSales: relayStylePagination(),
              transfers: relayStylePagination(),
              exports: relayStylePagination(),
            },
          },
          FinanceExportExecution: {
            fields: {
              invoices: relayStylePagination(),
              creditNotes: relayStylePagination(),
            },
          },
          PaperlessEmail: {
            fields: {
              processDocuments: relayStylePagination(['filters']),
              aiScanningJobs: relayStylePagination(),
              invoices: relayStylePagination(),
              creditNotes: relayStylePagination(),
            },
          },
          BillingGroup: {
            fields: {
              memberOrganisations: relayStylePagination(),
            },
          },
          Account: {
            fields: {
              children: relayStylePagination(),
              organisations: relayStylePagination(),
            },
          },
          OrganisationInterface: {
            fields: {
              selfAndDescendants: relayStylePagination(),
              flatAccountCodes: relayStylePagination(),
            },
          },
          Budget: {
            fields: {
              requisitions: relayStylePagination(),
            },
          },
        },
      })

      // Apollo Persistor
      const persistor = new CachePersistor({
        cache: apolloCache,
        storage: new LocalStorageWrapper(window.localStorage),
        debug: import.meta.env.VITE_APP_VERCEL_ENV === 'production',
        trigger: 'write',
        persistenceMapper: async (data) => {
          // Control what portions of the cache are persisted by passing the __typename into the array
          // Ex: dataPersistenceMapper(data, [...typenames])
          return dataPersistenceMapper(data, ['StockCount', 'StockTake', 'StockLocation', 'Preference'])
        },
      })

      // Restore the cache from localStorage BEFORE creating the Apollo Client
      await persistor.restore()

      setApolloPersistor(persistor)

      // Apollo Client
      setApolloClient(
        new ApolloClient({
          link: from([errorLink, operationNameLink, variableTransformLink, sentryLink, refererLink, uploadLink]),
          cache: apolloCache,
          devtools: {
            enabled: import.meta.env.MODE === 'development',
          },
        })
      )
    }

    init().catch(console.error)
  }, [])

  if (!apolloClient || !apolloPersistor) return <Spinner className="mt-12 h-28 md:h-32" data-testid="spinner" />
  return (
    <ApolloProvider client={apolloClient}>
      <ApolloPersistorProvider persistor={apolloPersistor}>{children}</ApolloPersistorProvider>
    </ApolloProvider>
  )
}

export default ApolloProviderWrapper
